// Direct Manager Discrepancy Audit — Runs list page (ADR-010 Phase 6).
//
// Page 1 of the list/detail pattern. Shows all audit runs, lets admins trigger
// a new one. Clicking a row navigates to the detail page.
//
// ADR-064: "Trigger now" is a source-choosing menu (Enova / Americas). The
// running-run guard is per-source — an Enova run in progress must not block an
// Americas trigger. Backend idempotency remains the real guard.

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlay } from 'react-icons/fa';
import { CircularProgress, Menu, MenuItem } from '@mui/material';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  MainSection,
  BackButton,
} from '../styles';
import { HeaderButton } from '../../common/styles';
import { useAbility } from '../../../contexts/AbilityContext';
import { useAlert } from '../../../contexts/AlertContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import { DISCREPANCY_RUN_STATUSES, DISCREPANCY_SOURCE_SYSTEM_OPTIONS } from '../../../constants/discrepancyMappingStatuses';
import { useRunsList } from '../../../hooks/useDirectManagerDiscrepancies';
import { directManagerDiscrepanciesApi } from '../../../services/api/directManagerDiscrepanciesApi';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import RunsFilterBar from './RunsFilterBar';
import RunsTable from './RunsTable';
import { LoadMoreButton } from './styles';

const PAGE_LIMIT = 50;

const RunsListPage = () => {
  const navigate = useNavigate();
  const ability = useAbility();
  const { showAlert } = useAlert();

  const canTrigger = ability.can(ACTIONS.CREATE, SUBJECTS.DIRECT_MANAGER_DISCREPANCY_RUN);

  const [filters, setFilters] = useState({
    sourceSystem: '',
    status: [],
    limit: PAGE_LIMIT,
  });
  const [triggering, setTriggering] = useState(false);
  const [triggerMenuAnchor, setTriggerMenuAnchor] = useState(null);

  const { items: runs, pageInfo, isLoading, isLoadingMore, error, loadMore, refetch } = useRunsList(filters);

  // ADR-064: per-source running-run guard, best-effort from the loaded list.
  const runningSourceSystems = useMemo(
    () => new Set(
      runs
        .filter((r) => r.status === DISCREPANCY_RUN_STATUSES.RUNNING)
        .map((r) => r.sourceSystem),
    ),
    [runs],
  );

  const allSourcesRunning = DISCREPANCY_SOURCE_SYSTEM_OPTIONS.every(
    (option) => runningSourceSystems.has(option.value),
  );

  const handleTriggerNow = async (option) => {
    setTriggerMenuAnchor(null);
    setTriggering(true);
    try {
      await directManagerDiscrepanciesApi.triggerRun({ sourceSystem: option.value });
      showAlert?.(`Discrepancy run triggered for ${option.label}`, 'success');
      refetch();
    } catch (err) {
      showAlert?.(err?.message || 'Failed to trigger run', 'error');
    } finally {
      setTriggering(false);
    }
  };

  const handleRowClick = (run) => {
    navigate(`/reports/direct-manager-discrepancies/runs/${run.id}`);
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Direct Representative Discrepancy Audit</Title>
        </HeaderLeft>
        {canTrigger && (
          <>
            <HeaderButton
              onClick={(event) => setTriggerMenuAnchor(event.currentTarget)}
              disabled={allSourcesRunning || triggering}
              title={allSourcesRunning
                ? 'A run is already in progress for every source system'
                : 'Trigger a new run — choose the source system'}
            >
              <FaPlay />
              {triggering ? 'Triggering…' : 'Trigger now'}
            </HeaderButton>
            <Menu
              anchorEl={triggerMenuAnchor}
              open={Boolean(triggerMenuAnchor)}
              onClose={() => setTriggerMenuAnchor(null)}
            >
              {DISCREPANCY_SOURCE_SYSTEM_OPTIONS.map((option) => {
                const sourceRunning = runningSourceSystems.has(option.value);
                return (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleTriggerNow(option)}
                    disabled={sourceRunning}
                    title={sourceRunning ? `A ${option.label} run is already in progress` : undefined}
                  >
                    {option.label}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        )}
      </Header>

      <Content>
        <MainSection>
          <RunsFilterBar
            sourceSystem={filters.sourceSystem}
            status={filters.status}
            onChange={(next) => setFilters((prev) => ({ ...prev, ...next }))}
          />

          <ResultsCount
            count={pageInfo?.totalCount || 0}
            singular="run"
            plural="runs"
          />

          {isLoading && <LoadingSpinner />}
          {!isLoading && error && (
            <EmptyState
              title="Failed to load runs"
              description={error?.message || 'Try refreshing the page.'}
            />
          )}
          {!isLoading && !error && runs.length === 0 && (
            <EmptyState
              title="No discrepancy runs yet"
              description={
                canTrigger
                  ? 'Use "Trigger now" to start the first run, or wait for the daily scheduled chain.'
                  : 'No runs to display. Daily runs will appear here once the scheduler runs.'
              }
            />
          )}
          {!isLoading && !error && runs.length > 0 && (
            <>
              <RunsTable runs={runs} onRowClick={handleRowClick} />
              {pageInfo?.hasNextPage && (
                <LoadMoreButton onClick={loadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                  Load More
                </LoadMoreButton>
              )}
            </>
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default RunsListPage;
